import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function BeKoerekort() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Kørekort og vægt", path: "/trailer-regler" },
    { name: "Stort trailerkørekort (BE)", path: "/be-koerekort" }
  ];

  const tocItems = [
    { id: "hvad-giver-be", title: "Hvad giver et BE-kørekort ret til?" },
    { id: "forskellen-b-plus-be", title: "Forskellen på B+ (kode 96) og BE" },
    { id: "hvad-koster-be", title: "Hvad koster et trailerkørekort?" },
    { id: "undervisningskrav", title: "Lovpligtig undervisning: Teori og kørsel" },
    { id: "ingen-teoriproeve", title: "Ingen skriftlig teoriprøve" },
    { id: "proeven", title: "Sådan foregår den praktiske køreprøve" },
    { id: "skaeringsdatoer", title: "Datoernes betydning: Før og efter 2013" }
  ];

  const faq = [
    {
      q: "Skal man til skriftlig teoriprøve for at tage BE-kørekort?",
      a: "Nej, der er ingen selvstændig skriftlig teoriprøve hos Færdselsstyrelsen for kategori BE. Du skal gennemføre 4 lektioners lovpligtig teori hos en autoriseret kørelærer, men prøven hos den prøvesagkyndige består alene af en mundtlig overhøring i teknisk kontrol efterfulgt af den praktiske køreprøve."
    },
    {
      q: "Hvor lang tid tager det at tage et BE-kørekort?",
      a: "De fleste gennemfører forløbet på 1 til 3 uger. Selve køre- og teoriundervisningen kan klares på få dage, og den samlede varighed afhænger primært af Færdselsstyrelsens aktuelle ventetid på praktiske prøver i dit lokalområde."
    },
    {
      q: "Hvor meget må man trække med et BE-kørekort?",
      a: "Med et BE-kørekort erhvervet efter 19. januar 2013 må du føre en personbil eller varebil på op til 3.500 kg tilladt totalvægt tilkoblet en trailer på op til 3.500 kg tilladt totalvægt. Vogntogets samlede tilladte totalvægt må altså nå helt op på 7.000 kg."
    },
    {
      q: "Skal man have en lægeattest for at udvide til BE?",
      a: "Ja, ansøgning om udvidelse af kørekortet til kategori BE kræver en lægeattest udstedt af din egen læge samt et godkendt pasfoto, som indleveres til Borgerservice."
    },
    {
      q: "Hvorfor vælger de fleste BE frem for B+?",
      a: "Prisforskellen hos køreskolerne mellem B+ og BE er typisk under 1.000 kr., og undervisningen er næsten identisk. Med BE opnår du den fulde grænse på 7.000 kg samlet vægt frem for 4.250 kg i B+, hvilket giver fuld fleksibilitet ved kørsel med tunge hestetrailere, maskintrailere og store campingvogne."
    }
  ];

  const sources = [
    { title: "Kørekortbekendtgørelsen (BEK nr. 1243 af 28/08/2020)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2020/1243" },
    { title: "Færdselsstyrelsen: Kørekort til påhængskøretøj til bil (B, B+, BE)", source: "fstyr.dk", url: "https://fstyr.dk/privat/koerekort/udvidelse-af-koerekort/koerekort-til-paahaengskoeretoej-til-bil" }
  ];

  return (
    <>
      <SEO 
        title="BE-kørekort: Regler, pris og prøve til trailerkørekort" 
        description="Alt om stort trailerkørekort (kategori BE): hvad du må trække, prisspænd for køreskole, lovpligtige lektioner og den praktiske køreprøve." 
        pathname="/be-koerekort"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />

        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Stort trailerkørekort (BE): Regler, pris og prøve
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          Et kategori BE-kørekort giver ret til at føre en personbil eller varebil på op til <strong>3.500 kg tilladt totalvægt</strong> tilkoblet en trailer på op til <strong>3.500 kg tilladt totalvægt</strong>. Vogntogets samlede tilladte totalvægt må nå helt op på <strong>7.000 kg</strong>.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          For at tage et BE-kørekort skal du i forvejen have et almindeligt B-kørekort. Undervisningen omfatter 4 teorilektioner, 6 praktiske kørelektioner og en praktisk køreprøve hos Færdselsstyrelsen. Der afholdes ingen skriftlig teoriprøve.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Kort overblik over kategori BE">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>• Maksimal tilladt totalvægt for bil: <strong>3.500 kg</strong></li>
            <li>• Maksimal tilladt totalvægt for trailer: <strong>3.500 kg</strong></li>
            <li>• Maksimal samlet vogntogsvægt: <strong>7.000 kg</strong></li>
            <li>• Undervisningskrav: <strong>4 teorilektioner + 6 kørelektioner</strong></li>
            <li>• Teoriprøve: <strong>Ingen skriftlig prøve</strong> (kun praktisk prøve)</li>
          </ul>
        </Callout>

        <h2 id="hvad-giver-be" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvad giver et BE-kørekort ret til?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når du har bestået den praktiske prøve til kategori BE hos Færdselsstyrelsen, må du føre:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Person- eller varebil med en tilladt totalvægt på højst 3.500 kg.</li>
          <li>Påhængskøretøj med en tilladt totalvægt på højst 3.500 kg.</li>
          <li>Vogntog med en samlet tilladt totalvægt på maksimalt 7.000 kg.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Med et BE-kort slipper du for den indviklede 3.500 kg-begrænsning for vogntoget, som gælder for det almindelige B-kørekort. Du må tilkoble enhver trailer op til 3.500 kg, så længe bilens registreringsattest og anhængertræk er godkendt til at trække den pågældende vægt. Læs mere om bilens grænser under <Link to="/hvor-meget-maa-man-traekke" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">hvor meget må man trække</Link>.
        </p>

        <h2 id="forskellen-b-plus-be" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Forskellen på B+ (kode 96) og BE
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Mange bilister er i tvivl om, hvorvidt de skal vælge det mindre B+-kørekort (EU-kode 96) eller det fulde BE-kørekort:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Parameter</th>
                <th className="py-2.5 pr-4 font-semibold">Kategori B+ (kode 96)</th>
                <th className="py-2.5 font-semibold">Kategori BE</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Maksimal vogntogsvægt</td>
                <td className="py-3 pr-4">4.250 kg</td>
                <td className="py-3 font-bold text-[#B58200]">7.000 kg</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Maksimal trailervægt</td>
                <td className="py-3 pr-4">Begrænset af de 4.250 kg</td>
                <td className="py-3 font-semibold text-[#2E3A46]">3.500 kg</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Obligatorisk teori</td>
                <td className="py-3 pr-4">4 teorilektioner</td>
                <td className="py-3">4 teorilektioner</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Obligatorisk kørsel</td>
                <td className="py-3 pr-4">4 kørelektioner</td>
                <td className="py-3">6 kørelektioner</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Praktisk prøve</td>
                <td className="py-3 pr-4">Køreprøve med mellemstort vogntog</td>
                <td className="py-3">Køreprøve med fuldt vogntog</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Typisk samlet merpris</td>
                <td className="py-3 pr-4">ca. 500 – 1.000 kr. billigere</td>
                <td className="py-3 text-[#5C636B]">Standardvalg hos køreskolerne</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Da tidsforbruget og omkostningerne ved undervisningen kun adskiller sig med to kørelektioner, vælger langt de fleste elever at tage det fulde BE-kort. Det sikrer, at man ikke senere kommer i klemme, hvis man skifter til en tungere bil eller køber en større campingvogn eller bådtrailer.
        </p>

        <h2 id="hvad-koster-be" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvad koster et trailerkørekort?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Den samlede pris for et BE-kørekort afhænger af køreskolens pakkesammensætning, geografi og dit individuelle behov for eventuelle ekstra lektioner. Nedenfor ses de typiske udgiftsposter:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Omkostningselement</th>
                <th className="py-2.5 pr-4 font-semibold">Typisk prisspænd</th>
                <th className="py-2.5 font-semibold">Modtager og vilkår</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Lovpakke (4 teori + 6 kørsel)</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">3.800 – 6.500 kr.</td>
                <td className="py-3 text-[#5C636B]">Køreskolen</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Statsligt prøvegebyr</td>
                <td className="py-3 pr-4">ca. 1.170 kr.</td>
                <td className="py-3 text-[#5C636B]">Færdselsstyrelsen</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Lægeattest</td>
                <td className="py-3 pr-4">400 – 700 kr.</td>
                <td className="py-3 text-[#5C636B]">Egen praktiserende læge</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Leje af vogntog til køreprøve</td>
                <td className="py-3 pr-4">800 – 1.800 kr.</td>
                <td className="py-3 text-[#5C636B]">Køreskolen</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Pasfoto til kørekort</td>
                <td className="py-3 pr-4">100 – 150 kr.</td>
                <td className="py-3 text-[#5C636B]">Borgerservice / fotograf</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Samlet prisniveau</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">6.200 – 10.500 kr.</td>
                <td className="py-3 font-semibold text-[#2E3A46]">For et standardforløb</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 id="undervisningskrav" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Lovpligtig undervisning: Teori og kørsel
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Undervisningen til kategori BE er strengt reguleret i Færdselsstyrelsens undervisningsplan og omfatter følgende obligatoriske moduler:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>4 teorilektioner (á 45 minutter):</strong> Grundig gennemgang af sammenkoblingsregler, vægtberegning, lasteevne, tilladt og faktisk totalvægt, kugletryk, bremsetyper (påløbsbremse og sikkerhedswire), dækregler og gældende hastighedsgrænser i Danmark og udlandet.</li>
          <li><strong>6 praktiske kørelektioner (á 45 minutter):</strong> Træning i korrekt sammen- og frakobling af vogntoget, sikkerhedskontrol af bremser, lys og dæk, præcis bakning ligeud og om hjørner udelukkende med sidespejle samt kørsel i tæt bymiljø og på landeveje.</li>
        </ul>

        <h2 id="ingen-teoriproeve" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Ingen skriftlig teoriprøve
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          En udbredt misforståelse er, at man skal op til en skriftlig billedteoriprøve for at erhverve BE-kort. Det er ikke tilfældet.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Der findes <strong>ingen skriftlig teoriprøve</strong> hos Færdselsstyrelsen for kategori BE. Teoriundervisningen afsluttes hos din kørelærer, som godkender din lektionsplan (logbog). Din teoretiske viden testes udelukkende mundtligt af den prøvesagkyndige umiddelbart før selve kørslen til den praktiske prøve.
        </p>

        <h2 id="proeven" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Sådan foregår den praktiske køreprøve
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Den praktiske prøve varer typisk mellem 45 og 60 minutter og forløber i fire faste faser:
        </p>

        <ol className="list-decimal list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Kontrol og sammenkobling:</strong> Den prøvesagkyndige beder dig gennemgå trailerens udstyr – f.eks. kontrollere sikkerhedswirens montering, påløbsbremsens funktion, lygternes virkemåde, dæktryk, slidbane og reflekser. Du kan også blive bedt om at demonstrere korrekt fra- og tilkobling.</li>
          <li><strong>Mundtlig overhøring:</strong> Du skal kunne redegøre for reglerne om vogntogets vægtbegrænsninger, kugletrykkets betydning for stabiliteten og korrekt sikring af lasten efter færdselslovens § 82.</li>
          <li><strong>Manøvrer i lukket område:</strong> Du skal demonstrere sikker bakning med vogntoget over en strækning på mindst 20 meter samt foretage en kontrolleret bakning om et gadehjørne (til højre eller venstre) alene ved brug af bilens sidespejle.</li>
          <li><strong>Kørsel i trafikken:</strong> Kørsel på offentlig vej i by, på landevej og eventuelt motorvej, hvor den prøvesagkyndige bedømmer din placering på kørebanen, orientering i spejle, hastighedsafpasning og hensyn til vogntogets forøgede længde og svingningsradius.</li>
        </ol>

        <h2 id="skaeringsdatoer" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Datoernes betydning: Før og efter 2013
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Den 19. januar 2013 trådte det 3. EU-kørekortdirektiv i kraft, hvilket medførte en ændring i rettighederne for kategori BE:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>BE erhvervet FØR 19. januar 2013:</strong> Giver ret til at føre en personbil på op til 3.500 kg med en trailer, hvis tilladte totalvægt overstiger 3.500 kg, forudsat traileren er typegodkendt med gennemgående bremser (f.eks. trykluft). Der er her intet loft på trailerens vægt, udover hvad trækbilen må trække.</li>
          <li><strong>BE erhvervet EFTER 19. januar 2013:</strong> Er underlagt et fast lovloft på maksimalt 3.500 kg for trailerens tilladte totalvægt. Skal du trække en trailer over 3.500 kg, kræver det i dag lastbilkørekort (kategori C1E eller CE).</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs mere om de historiske skæringsdatoer under <Link to="/koerekort-og-trailer" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">kørekort og trailer</Link>, og se hvordan du undgår overvægt i <Link to="/trailer-totalvaegt" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer totalvægt</Link> og <Link to="/trailer-bremser" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailerbremser</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
